// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, StyleSheet } from 'react-native';
import { Stack, useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';

import DeleteAccountModal from '@/components/DeleteAccountModal';
import { Text, View } from '@/components/Themed';
import { buildLogoutUrl, POST_LOGOUT_REDIRECT, revokeRefreshToken } from '@/auth/oidc';
import { useDeleteAccount } from '@/hooks/useDeleteAccount';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useUser } from './user-context';

/**
 * The account deletion page, served at goffy.app/delete-account.
 *
 * Google Play requires a deletion route reachable on the open web, not only the control inside the
 * app, and a reviewer opens it signed out — so this route is in the public allowlist in
 * `useAuthFlow` and explains the process to a visitor with no session. A signed-in visitor gets the
 * real button, reusing the same hook and confirm dialog as the profile screen so there is one
 * deletion path rather than two that can drift.
 */
const LAST_UPDATED = '2026-08-07';

const SECTIONS = [
  ['deleteAccountPage.removedH', 'deleteAccountPage.removedP'],
  ['deleteAccountPage.keptH', 'deleteAccountPage.keptP'],
  ['deleteAccountPage.inAppH', 'deleteAccountPage.inAppP'],
  ['deleteAccountPage.signedOutH', 'deleteAccountPage.signedOutP'],
] as const;

const CONTACT_EMAIL = 'privacy@goffy.app';

export default function DeleteAccountScreen() {
  const { t } = useI18n();
  const { user, setUser } = useUser();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Same best-effort teardown as the profile screen: the account is gone server-side by the time
  // this runs, so clear the local session, leave, then tidy up Keycloak.
  const finishAccountDeletion = useCallback(async () => {
    const idTokenHint = user?.idToken ?? user?.token ?? null;
    const refreshToken = user?.refreshToken ?? null;
    setConfirmOpen(false);
    setUser(null);
    router.replace('/');
    if (refreshToken) {
      try {
        await revokeRefreshToken(refreshToken);
      } catch {
        // Deleting the Keycloak user already invalidated the token.
      }
    }
    const logoutUrl = buildLogoutUrl(idTokenHint);
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
        window.location.href = logoutUrl;
      } else {
        await WebBrowser.openAuthSessionAsync(logoutUrl, POST_LOGOUT_REDIRECT);
      }
    } catch {
      // The local session is cleared either way.
    }
  }, [router, setUser, user?.idToken, user?.refreshToken, user?.token]);

  const { deleteAccount, loading, error } = useDeleteAccount({
    apiBaseUrl,
    userId: user?.id,
    token: user?.token,
    onDeleted: finishAccountDeletion,
  });

  return (
    <>
      <Stack.Screen options={{ title: t('deleteAccountPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('deleteAccountPage.title')}</Text>
        <Text style={styles.updated}>{t('deleteAccountPage.updated', { date: LAST_UPDATED })}</Text>
        <Text style={styles.intro}>{t('deleteAccountPage.intro')}</Text>

        {SECTIONS.map(([heading, body]) => (
          <View key={heading} style={styles.section}>
            <Text style={styles.heading}>{t(heading)}</Text>
            <Text style={styles.paragraph}>{t(body)}</Text>
          </View>
        ))}

        {user?.token ? (
          <Pressable
            style={styles.dangerButton}
            testID="delete-account-page-open"
            accessibilityLabel={t('profile.deleteAccount')}
            onPress={() => setConfirmOpen(true)}
          >
            <Text style={styles.dangerButtonText}>{t('profile.deleteAccount')}</Text>
          </Pressable>
        ) : (
          <Pressable
            style={styles.contactButton}
            accessibilityRole="link"
            testID="delete-account-page-mail"
            onPress={() => Linking.openURL(`mailto:${CONTACT_EMAIL}`)}
          >
            <Text style={styles.contactButtonText}>{CONTACT_EMAIL}</Text>
          </Pressable>
        )}
      </ScrollView>

      <DeleteAccountModal
        visible={confirmOpen}
        pending={loading}
        errorText={error}
        onConfirm={() => {
          void deleteAccount();
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 48,
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
  },
  updated: {
    fontSize: 14,
    opacity: 0.7,
    marginTop: 2,
    marginBottom: 16,
  },
  intro: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 20,
  },
  section: {
    marginBottom: 18,
  },
  heading: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 6,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 24,
  },
  dangerButton: {
    marginTop: 8,
    alignSelf: 'flex-start',
    borderRadius: 12,
    backgroundColor: '#7f1d1d',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  dangerButtonText: {
    color: '#fecaca',
    fontSize: 14,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  contactButton: {
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  contactButtonText: {
    fontSize: 16,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
});
